import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { Button, FileUpload, Select, CountryPicker } from "@/shared/ui";
import { toast } from "sonner";
import { useAuth } from "../../../contexts/AuthContext";
import {
  extractTextFromDocument,
  validateDocumentFile,
  isDocumentTooLong,
  truncateDocument,
} from "@/shared/core";
import { analyzeDocumentWithGroq } from "@/shared/ai";
import {
  createDocumentReview,
  updateReviewAnalysis,
  updateReviewStatus,
  incrementUsage,
} from "@/shared/db";
import { uploadFile } from "@/shared/db";
import { checkLimit } from "@/shared/core";
import { useCountries } from "@/hooks/useCountries";
import { isCountryEnabled } from "@/shared/config";
import type { UploadDocumentModalProps } from "../types/component.types";

export const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { getCountryName } = useCountries();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentType, setDocumentType] = useState("");
  const [country, setCountry] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState<string>("");
  const [extractedText, setExtractedText] = useState<string>("");
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [step, setStep] = useState<"upload" | "preview" | "analyzing">(
    "upload"
  );

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files);
  };

  // Phase 1: Upload and Extract
  const handleUploadAndExtract = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error(t("documentReview.errors.mustLogin"));
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error(t("documentReview.errors.noFile"));
      return;
    }

    if (!documentType || !country) {
      toast.error(t("documentReview.errors.missingFields"));
      return;
    }

    setIsProcessing(true);
    setProcessingStep(t("documentReview.steps.uploading"));

    try {
      const file = selectedFiles[0];

      // Validate file
      const validation = validateDocumentFile(file);
      if (!validation.isValid) {
        toast.error(validation.error || t("documentReview.errors.invalidFile"));
        setIsProcessing(false);
        return;
      }

      // Validate country is enabled (from countries_config)
      const enabled = await isCountryEnabled(country);
      if (!enabled) {
        toast.error(t("documentReview.errors.countryNotEnabled"));
        setIsProcessing(false);
        return;
      }

      // Step 1: Upload file
      const fileResult = await uploadFile({
        bucket: "document-reviews",
        folder: user.id,
        file,
      });

      // Step 2: Extract text
      setProcessingStep(t("documentReview.steps.extracting"));
      const extracted = await extractTextFromDocument(file);
      let textContent = extracted.text;

      if (isDocumentTooLong(extracted)) {
        toast.warning(t("documentReview.warnings.documentTruncated"));
        textContent = truncateDocument(extracted).text;
      }

      // Step 3: Create initial DB record
      setProcessingStep(t("documentReview.steps.creating"));
      const review = await createDocumentReview({
        user_id: user.id,
        document_name: file.name,
        document_type: documentType,
        country: country,
        file_path: fileResult.path,
        file_url: fileResult.url,
        file_size: file.size,
        file_mime_type: file.type,
        original_content: textContent,
        credits_used: 1,
      });

      setReviewId(review.id);
      setExtractedText(textContent);
      setStep("preview");
      toast.success(t("documentReview.success.extracted"));
    } catch (error: any) {
      toast.error(t("documentReview.errors.processingFailed"));
    } finally {
      setIsProcessing(false);
      setProcessingStep("");
    }
  };

  // Phase 2: Analyze with Groq
  const handleStartAnalysis = async () => {
    if (!reviewId || !extractedText || !user) return;

    // Check limit before analysis
    try {
      const limitCheck = await checkLimit(user.id, "document_review");
      if (!limitCheck.allowed) {
        toast.error(t("documentReview.errors.limitReached"));
        return;
      }
    } catch (error) {
      console.error("Error checking limit:", error);
    }

    setStep("analyzing");
    setIsProcessing(true);
    setProcessingStep(t("documentReview.steps.analyzing"));

    const ANALYSIS_TIMEOUT_MS = 90_000;

    try {
      const analysisPromise = analyzeDocumentWithGroq({
        content: extractedText,
        documentType: documentType,
        country: country,
        documentName: selectedFiles[0]?.name,
        language: i18n.language === "en" ? "en" : "es",
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("analysis_timeout")),
          ANALYSIS_TIMEOUT_MS
        )
      );

      const analysisResult = await Promise.race([analysisPromise, timeoutPromise]);

      // Update review
      await updateReviewAnalysis({
        id: reviewId,
        analysis_result: analysisResult,
        analysis_status: "completed",
        analyzed_at: new Date().toISOString(),
      });

      // Increment usage
      try {
        await incrementUsage(user.id, "document_review", 1);
      } catch {
        /* usage increment failed, continue */
      }

      toast.success(t("documentReview.success.analyzed"));

      // Track AI review usage
      import("@/utils/analytics").then(({ analytics }) => {
        analytics.aiReviewUsed();
      });

      onSuccess?.(reviewId);
      handleClose();
    } catch (error: any) {
      await updateReviewStatus(reviewId, "failed");
      const isTimeout = error?.message === "analysis_timeout";
      toast.error(
        isTimeout
          ? t("documentReview.errors.analysisTimeout")
          : t("documentReview.errors.analysisFailed")
      );
      setStep("preview"); // Allow retry
    } finally {
      setIsProcessing(false);
      setProcessingStep("");
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedFiles([]);
      setDocumentType("");
      setCountry("");
      setProcessingStep("");
      setExtractedText("");
      setReviewId(null);
      setStep("upload");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-juridico/60 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
        />

        {/* Modal Content */}
        <div className="relative z-10 w-full max-w-[calc(100vw-2rem)] sm:max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all">
          {/* Header */}
          <div className="px-6 py-4 border-b border-tiber-100 flex items-center justify-between bg-white sticky top-0 z-20">
            <div>
              <h3 className="text-xl font-bold text-juridico">
                {step === "upload"
                  ? t("documentReview.uploadDocument")
                  : t("documentReview.previewAndAnalyze")}
              </h3>
              <p className="text-sm text-titan mt-0.5">
                {step === "upload"
                  ? t("documentReview.uploadDescription")
                  : t("documentReview.confirmAndProcess")}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="p-2 text-titan hover:text-tiber-700 hover:bg-romance-50 rounded-full cursor-pointer transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            {step === "upload" ? (
              <form onSubmit={handleUploadAndExtract} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-tiber-800 mb-3">
                    {t("documentReview.selectFile")} *
                  </label>
                  <FileUpload
                    accept={{
                      "application/pdf": [".pdf"],
                      "application/msword": [".doc"],
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                        [".docx"],
                      "text/plain": [".txt"],
                    }}
                    maxSize={10 * 1024 * 1024}
                    maxFiles={1}
                    onFilesSelected={handleFilesSelected}
                    disabled={isProcessing}
                    label={t("documentReview.dragDropFile")}
                  />
                </div>

                {/* Document Information Section */}
                <div className="bg-tiber-50 border-l-4 border-tiber-600 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <InformationCircleIcon className="h-5 w-5 text-tiber-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-tiber-900 mb-1">
                        {t("documentReview.infoSection.title")}
                      </h4>
                      <p className="text-sm text-tiber-700">
                        {t("documentReview.infoSection.description")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-tiber-900 mb-2">
                      {t("documentReview.documentType")} *
                    </label>
                    <p className="text-xs text-tiber-700 mb-3">
                      {t("documentReview.documentTypeHelper")}
                    </p>
                    <Select
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                      disabled={isProcessing}
                      required
                      placeholder={t("documentReview.selectDocumentType")}
                      options={[
                        {
                          value: "Contrato",
                          label: t("documentReview.documentTypes.contract"),
                        },
                        {
                          value: "Convenio",
                          label: t("documentReview.documentTypes.agreement"),
                        },
                        {
                          value: "Política",
                          label: t("documentReview.documentTypes.policy"),
                        },
                        {
                          value: "Términos y Condiciones",
                          label: t("documentReview.documentTypes.terms"),
                        },
                        {
                          value: "Otro",
                          label: t("documentReview.documentTypes.other"),
                        },
                      ]}
                    />
                  </div>

                  <div>
                    <CountryPicker
                      label={t("documentReview.legalJurisdiction")}
                      value={country}
                      onChange={setCountry}
                      scrollHorizontal
                      className={isProcessing ? "pointer-events-none opacity-60" : undefined}
                    />
                    <p className="text-xs text-tiber-700 mt-2">
                      {t("documentReview.jurisdictionHelper")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-6 border-t border-tiber-100">
                  <Button variant="ghost" type="button" onClick={handleClose}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      isProcessing ||
                      selectedFiles.length === 0 ||
                      !documentType ||
                      !country
                    }
                  >
                    {isProcessing ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {processingStep || t("common.processing")}
                      </div>
                    ) : (
                      t("documentReview.uploadAndPreview")
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col md:flex-row gap-8 min-h-[400px]">
                {/* Left: Preview */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-tiber-800 uppercase tracking-wider">
                      {t("documentReview.extractedContent")}
                    </h4>
                    <span className="text-xs font-light text-titan">
                      {extractedText.length} {t("common.characters")}
                    </span>
                  </div>
                  <div className="bg-romance-50 rounded-xl border border-tiber-200 p-4 h-[400px] overflow-y-auto text-sm font-light text-tiber-700 leading-relaxed whitespace-pre-wrap">
                    {extractedText}
                  </div>
                </div>

                {/* Right: Actions */}
                <div className="w-full md:w-80 space-y-6">
                  <div className="p-5 bg-tiber-50 rounded-2xl border border-tiber-100 space-y-4">
                    <h4 className="font-bold text-tiber-900">
                      {t("documentReview.readyForAnalysis")}
                    </h4>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2 text-sm font-light text-tiber-700">
                        <div className="h-1.5 w-1.5 rounded-full bg-tiber-500" />
                        {t("documentReview.readyFile")}:{" "}
                        {selectedFiles[0]?.name}
                      </li>
                      <li className="flex items-center gap-2 text-sm font-light text-tiber-700">
                        <div className="h-1.5 w-1.5 rounded-full bg-tiber-500" />
                        {t("documentReview.readyType")}: {documentType}
                      </li>
                      <li className="flex items-center gap-2 text-sm font-light text-tiber-700">
                        <div className="h-1.5 w-1.5 rounded-full bg-tiber-500" />
                        {t("documentReview.readyCountry")}:{" "}
                        {getCountryName(country) || country}
                      </li>
                    </ul>

                    <button
                      onClick={handleStartAnalysis}
                      disabled={isProcessing}
                      className="w-full py-4 bg-tiber-600 text-white rounded-xl font-bold shadow-xl shadow-tiber-200 hover:bg-tiber-700 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                    >
                      {isProcessing ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          {processingStep || t("documentReview.analyzing")}
                        </div>
                      ) : (
                        t("documentReview.processAnalysis")
                      )}
                    </button>

                    <p className="text-[10px] text-tiber-400 text-center leading-tight">
                      {t("documentReview.analysisWarning")}
                    </p>
                  </div>

                  <button
                    onClick={() => setStep("upload")}
                    disabled={isProcessing}
                    className="w-full py-2.5 text-sm font-medium text-titan hover:text-tiber-800 border border-transparent hover:border-tiber-200 rounded-xl transition-all"
                  >
                    {t("documentReview.uploadOtherFile")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
